interface LocationInfo {
  country: string;
  countryCode: string;
  isIndia: boolean;
}

class LocationService {
  private locationCache: LocationInfo | null = null;
  private fallbackToIndia = true; // Fallback to India for better UX

  async getUserLocation(): Promise<LocationInfo> {
    // Return cached result if available
    if (this.locationCache) {
      return this.locationCache;
    }

    try {
      // Try to get location from IP geolocation API with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch('https://ipapi.co/json/', {
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        
        const locationInfo: LocationInfo = {
          country: data.country_name || 'India',
          countryCode: data.country_code || 'IN',
          isIndia: data.country_code === 'IN'
        };
        
        this.locationCache = locationInfo;
        return locationInfo;
      }
    } catch (error) {
      console.warn('Failed to detect user location:', error);
    }

    // Fallback to India if detection fails
    const fallbackLocation: LocationInfo = {
      country: 'India',
      countryCode: 'IN',
      isIndia: this.fallbackToIndia
    };
    
    this.locationCache = fallbackLocation;
    return fallbackLocation;
  }

  // Method to check if user should see pricing (India) or trial request (others)
  async shouldShowPricing(): Promise<boolean> {
    const location = await this.getUserLocation();
    return location.isIndia;
  }

  // Method to get location for trial request
  async getLocationForTrialRequest(): Promise<{ country: string; countryCode: string }> {
    const location = await this.getUserLocation();
    return {
      country: location.country,
      countryCode: location.countryCode
    };
  }

  // Clear cache (useful for testing)
  clearCache(): void {
    this.locationCache = null;
  }

  // Force set location (useful for testing)
  setLocation(country: string, countryCode: string): void {
    this.locationCache = {
      country,
      countryCode,
      isIndia: countryCode === 'IN'
    };
  }
}

export const locationService = new LocationService();
export type { LocationInfo }; 